import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as GroupItemTemplateActions from "../../../actions/groupItemTemplate";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import { CheckboxFormInput, NumberFormInput, SelectFormInput, TextAreaFormInput, TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { CollectionPage } from "../../../containers/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { GroupItemTemplateBreadcrumbs, GroupItemTemplateCreateBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { SettingsGroupsSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { GroupSettingsLayout } from "../../index";

import "./GroupItemTemplatePage.scss";

interface GroupItemPageProps {
    form: Dtos.GroupItemTemplate & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    groupItemTemplate: Dtos.GroupItemTemplate;
    collection: Dtos.Collection;
    lookups: Dtos.Lookup[];
    loadingGroupItemTemplate: boolean;
    loadGroupItemTemplateFailure: boolean;
    loadGroupItemTemplateErrorCode: string;
    savingGroupItemTemplate: boolean;
    saveGroupItemTemplateSuccess: boolean;
    saveGroupItemTemplateFailure: boolean;
    creatingGroupItemTemplate: boolean;
    createGroupItemTemplateSuccess: boolean;
    validatingGroupItemTemplate: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    itemComponentTypes: Dtos.ItemComponentType[];
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    clearGroupItemTemplate: GroupItemTemplateActions.IGroupItemTemplateClearActionCreator;
    loadGroupItemTemplate: GroupItemTemplateActions.IGroupItemTemplateLoadByIdActionCreator;
    saveGroupItemTemplate: GroupItemTemplateActions.IGroupItemTemplateSaveActionCreator;
    createGroupItemTemplate: GroupItemTemplateActions.IGroupItemTemplateCreateActionCreator;
    validateGroupItemTemplate: GroupItemTemplateActions.IGroupItemTemplateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
}

const reduxFormName: string = "reduxForms.groupItemTemplate";

const GroupItemPage: FC<GroupItemPageProps> = ({
    form,
    formState,
    formProperties,
    groupItemTemplate,
    collection,
    lookups,
    loadingGroupItemTemplate,
    loadGroupItemTemplateFailure,
    loadGroupItemTemplateErrorCode,
    savingGroupItemTemplate,
    saveGroupItemTemplateSuccess,
    saveGroupItemTemplateFailure,
    creatingGroupItemTemplate,
    createGroupItemTemplateSuccess,
    validatingGroupItemTemplate,
    validationFailures,
    permissions,
    itemComponentTypes,
    loadingItemComponentTypes,
    // loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    clearGroupItemTemplate,
    loadGroupItemTemplate,
    saveGroupItemTemplate,
    createGroupItemTemplate,
    validateGroupItemTemplate,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearItemComponentTypes,
    loadItemComponentTypes
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{groupId: string;}>();
    const groupIdNumber = parseInt(groupId, 10);
    const { groupItemTemplateId } = useParams<{groupItemTemplateId: string;}>();
    const groupItemTemplateIdNumber = parseInt(groupItemTemplateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.GroupItemTemplate = {} as Dtos.GroupItemTemplate;

        if (!isNaN(groupIdNumber)) {
            extraData.groupId = groupIdNumber;
        }

        loadForm(reduxFormName, Object.assign({ ...groupItemTemplate }, extraData));
    };


    // Load group item template
    useEffect(() => {
        if (
            groupItemTemplateIdNumber &&
            !groupItemTemplate &&
            !loadingGroupItemTemplate
        ) {
            setDrawerItems(SettingsGroupsSubMenu(permissions));
            setBreadcrumbs(
                GroupItemTemplateBreadcrumbs(groupItemTemplateIdNumber)
            );
            loadGroupItemTemplate(groupItemTemplateIdNumber);
        } else if (
            !groupItemTemplateIdNumber &&
            !creatingGroupItemTemplate &&
            !createGroupItemTemplateSuccess
        ) {
            createGroupItemTemplate(isNaN(groupIdNumber) ? undefined : groupIdNumber);
            setBreadcrumbs(
                GroupItemTemplateCreateBreadcrumbs(isNaN(groupIdNumber) ? undefined : groupIdNumber)
            );
            setDrawerItems([]);
        } else if (groupItemTemplate && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }

        if (!itemComponentTypes && !loadingItemComponentTypes) {
            loadItemComponentTypes();
        }
    }, [
        groupItemTemplateIdNumber,
        groupItemTemplate,
        setDrawerItems,
        SettingsGroupsSubMenu,
        permissions,
        setBreadcrumbs,
        GroupItemTemplateBreadcrumbs,
        loadGroupItemTemplate,
        createGroupItemTemplate,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingGroupItemTemplate,
        creatingGroupItemTemplate,
        createGroupItemTemplateSuccess,
        GroupItemTemplateCreateBreadcrumbs,
        itemComponentTypes,
        loadingItemComponentTypes,
        loadItemComponentTypes,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupItemTemplateFailure) {
            navigate(`/error/${loadGroupItemTemplateErrorCode}`);
        }
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        loadGroupItemTemplateFailure,
        loadGroupItemTemplateErrorCode,
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (groupItemTemplateIdNumber && groupItemTemplate?.name) {
            setBreadcrumbs(
                GroupItemTemplateBreadcrumbs(
                    groupItemTemplateIdNumber,
                    isNaN(groupIdNumber) ? undefined : groupIdNumber,
                    groupItemTemplate?.name
                )
            );
        }
    }, [
        groupItemTemplateIdNumber,
        groupItemTemplate,
        setBreadcrumbs,
        GroupItemTemplateBreadcrumbs,
    ]);

    // Reload group item template after saving
    useEffect(() => {
        if (!savingGroupItemTemplate && saveGroupItemTemplateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Group item template saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = groupIdNumber
                    ? `/settings/group/itemtemplates/${groupIdNumber}`
                    : "/settings/group/itemtemplates";
                navigate(url);
            } else if (!groupItemTemplateIdNumber) {
                navigate(`/settings/group/itemtemplates/${groupItemTemplate?.groupId}/template/${groupItemTemplate?.id}`);
                setInitalised(false);
                clearGroupItemTemplate();
            }
        }
    }, [
        savingGroupItemTemplate,
        saveGroupItemTemplateSuccess,
        groupItemTemplateIdNumber,
        navigate,
        clearGroupItemTemplate,
        saveAndReturn,
        groupItemTemplate,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingGroupItemTemplate && saveGroupItemTemplateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingGroupItemTemplate, saveGroupItemTemplateFailure]);

    // Cleanup effect to clear the group item template on component unmount
    useEffect(() => {
        return () => {
            clearGroupItemTemplate();
            clearItemComponentTypes();
            resetForm(reduxFormName);
        };
    }, [
        clearGroupItemTemplate,
        clearItemComponentTypes,
        resetForm,
        reduxFormName,
    ]);

    const onCancel = () => {
        let url: string = groupIdNumber
            ? `/settings/group/itemtemplates/${groupIdNumber}`
            : "/settings/group/itemtemplates";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.GroupItemTemplate) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.GroupItemTemplate = {} as Dtos.GroupItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!data?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            await saveGroupItemTemplate(
                Object.assign({ ...data }, changes),
                Object.assign({ ...collection }, collectionChanges)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.GroupItemTemplate = {} as Dtos.GroupItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!form?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            if (form && hasBeenSubmitted && !validatingGroupItemTemplate) {
                await validateGroupItemTemplate(
                    Object.assign({ ...form }, changes),
                    Object.assign({ ...collection }, collectionChanges)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderCollection = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.GroupItemTemplatesUpdate,
                Dtos.Permission.GroupItemTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingGroupItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        if (form && !loadingGroupItemTemplate && itemComponentTypes) {
            return (
                <CollectionPage
                    itemComponentTypeId={
                        itemComponentTypes?.find((x) => x.value === "Group")?.id
                    }
                    collectionId={form.collectionId || 0}
                    readonly={formDisabled}
                />
            );
        }
    }, [
        form,
        permissions,
        savingGroupItemTemplate,
        formState,
        loadingGroupItemTemplate,
        itemComponentTypes,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.GroupItemTemplatesUpdate,
                Dtos.Permission.GroupItemTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingGroupItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            details: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="card card-shadow">
                        <div className="form-row pt-4 pb-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        name: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        description: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextAreaFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                rows: 3,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        groupItemTemplateCategoryId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "GroupItemTemplate",
                                                            "GroupItemTemplateCategoryId",
                                                            lookups
                                                        ),
                                                        form.groupItemTemplateCategoryId
                                                    ),
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        rank: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: NumberFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pt-sm-0 pt-md-2 pt-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        groupId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled || !isNaN(groupIdNumber),
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "GroupItemTemplate",
                                                            "GroupId",
                                                            lookups
                                                        ),
                                                        form.groupId
                                                    ),
                                                clearable: true,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        allowIntraVersions: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow Intra Version",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                            changeAction: (_model, value, _form, _parentModel) => {
                                                changeForm(`${reduxFormName}.allowIntraVersions`, value);
                                                if (!value) {
                                                    changeForm(`${reduxFormName}.initialVersion`, null);
                                                }
                                            },
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        initialVersion: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled || !form.allowIntraVersions,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row pt-2 pb-4 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        allowVersionChanges: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow version label changes",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "groupItemTemplate",
                                    {
                                        active: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Active",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pt-sm-0 pt-md-2 pt-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                    </div>
                ),
            },
            components: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Components</h1>
                        {renderCollection}
                    </>
                ),
            },
        };

        return (
            <div>
                <h1>Group Item Template</h1>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "groupItemTemplate",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingGroupItemTemplate,
        formState,
        lookups,
        generateOptionsFromLookup,
        findLookup,
        renderCollection,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <GroupSettingsLayout
            permissions={permissions}
            permission={Dtos.Permission.GroupItemTemplatesView}
            loading={
                loadingGroupItemTemplate ||
                creatingGroupItemTemplate ||
                savingGroupItemTemplate
            }
        >
            {renderContent}
        </GroupSettingsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<GroupItemPageProps> => {
    let groupItemTemplate: Dtos.GroupItemTemplate | undefined = 
        !(state.groupItemTemplate.formData instanceof Array)
            ? state.groupItemTemplate.formData
            : undefined;
    let collection: Dtos.Collection | undefined = 
        !(state.reduxForms.collection instanceof Array)
            ? state.reduxForms.collection
            : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];
    let itemComponentTypes = state.itemComponentTypes.formData instanceof Array
        ? state.itemComponentTypes.formData
        : undefined;

    return {
        groupItemTemplate: groupItemTemplate!,
        collection: collection!,
        loadingGroupItemTemplate:
            state.groupItemTemplate.loadState &&
            state.groupItemTemplate.loadState.status == RequestState.Pending,
        loadGroupItemTemplateFailure:
            state.groupItemTemplate.loadState &&
            state.groupItemTemplate.loadState.status == RequestState.Failure,
        loadGroupItemTemplateErrorCode: state.groupItemTemplate.loadState.errorCode,
        savingGroupItemTemplate:
            state.groupItemTemplate.saveState &&
            state.groupItemTemplate.saveState.status == RequestState.Pending,
        saveGroupItemTemplateSuccess:
            state.groupItemTemplate.saveState &&
            state.groupItemTemplate.saveState.status == RequestState.Success,
        saveGroupItemTemplateFailure:
            state.groupItemTemplate.saveState &&
            state.groupItemTemplate.saveState.status == RequestState.Failure,
        creatingGroupItemTemplate:
            state.groupItemTemplate.createState &&
            state.groupItemTemplate.createState.status == RequestState.Pending,
        createGroupItemTemplateSuccess:
            state.groupItemTemplate.createState &&
            state.groupItemTemplate.createState.status == RequestState.Success,
        validatingGroupItemTemplate:
            state.groupItemTemplate.validateState &&
            state.groupItemTemplate.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.groupItemTemplate.lookups,
        form: state.reduxForms.groupItemTemplate,
        formState: state.groupItemTemplate.formState,
        formProperties: state.groupItemTemplate.formProperties,
        validationFailures: groupItemTemplate
            ? state.groupItemTemplate.validationFailures
            : undefined,
        itemComponentTypes: itemComponentTypes,
        loadingItemComponentTypes:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode: state.itemComponentTypes.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<GroupItemPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadGroupItemTemplate: bindActionCreators(GroupItemTemplateActions.LoadGroupItemTemplateById, dispatch),
        clearGroupItemTemplate: bindActionCreators(GroupItemTemplateActions.Clear, dispatch),
        saveGroupItemTemplate: bindActionCreators(GroupItemTemplateActions.SaveGroupItemTemplate, dispatch),
        createGroupItemTemplate: bindActionCreators(GroupItemTemplateActions.CreateGroupItemTemplate, dispatch),
        validateGroupItemTemplate: bindActionCreators(GroupItemTemplateActions.ValidateGroupItemTemplate, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadItemComponentTypes: bindActionCreators(ItemComponentTypeActions.LoadItemComponentType, dispatch),
        clearItemComponentTypes: bindActionCreators(ItemComponentTypeActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupItemPage);
